/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import {
    Button,
    Provider,
    defaultTheme,
} from "@adobe/react-spectrum";
import AdobeIcon from "icons/AdobeIcon";
import AnalyticsConstants from "app/constants/AnalyticsConstants";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import SideMenuCSS from "app/preview/react-component/header/presentation/side-menu/SideMenu.css";
import { dispatchUIActionClickEvent } from "app/util/AppUtil";
import { UIActionPositionTypes } from "bundle/constants/UIActionConstants";

// The following functions will be removed when AJS fixes issue with legal dialog
function suppressZIndex(className) {
    try {
        document.getElementsByClassName(className)[0].setAttribute("style", "z-index: 0 !important");
    } catch (e) {
        // element not found
    }
}

function disableElementById(id) {
    try {
        document.getElementById(id).disabled = true;
    } catch (e) {
        // id not found
    }
}

function disableElementByAriaLabel(ariaLabel) {
    try {
        document.querySelector(`[aria-label='${ariaLabel}']`).disabled = true;
    } catch (e) {
        // aria label not found
    }
}

function closeSearchBox() {
    try {
        document.querySelector("[aria-label='Close search box']").click();
    } catch (e) {
        // aria label not found
    }
}

function closeSideMenu() {
    try {
        document.querySelector("[aria-label='More actions']").click();
    } catch (e) {
        // side menu not found
    }
}

function onClickHandler() {
    const appStore = ServiceRegistry.getService("AppStore");
    const analyticsProviderService = ServiceRegistry.getService("AnalyticsProviderService");
    analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES);
    // the following code will be removed when AJS fixes the issue with the legal dialog
    setTimeout(() => {
        appStore.setLegalDialogOpen(true);
    }, 300);
    if (appStore.isSizedPreset && appStore.inFullScreen) {
        closeSideMenu();
        ["bookmarks", "thumbnails", "PageNumberUIModern", "exitFullScreen", "documentSearch", "change-view-mode"]
            .forEach(disableElementById);
        // set previous disabled values
        try {
            appStore.previousPageDisabled = document.querySelector("[aria-label='Go to previous page']").disabled;
            appStore.nextPageDisabled = document.querySelector("[aria-label='Go to next page']").disabled;
            appStore.zoomInDisabled = document.querySelector("[aria-label='Zoom page in']").disabled;
            appStore.zoomOutDisabled = document.querySelector("[aria-label='Zoom page out']").disabled;
        } catch (e) {
            // aria label not found
        }
        ["Go to previous page", "Go to next page", "Zoom page in", "Zoom page out", "More actions"]
            .forEach(disableElementByAriaLabel);
        closeSearchBox();
    }
    const r = document.querySelector(":root");
    r.style.setProperty("--z-index-quick-tools", 0);
    if (appStore.isFWPreset) {
        ["QuickTools__QuickTools___zTRYz", "CommentListContainer__modernPageLevelCommentContainer___RbtGq",
            "ModernEditableTextComponent__modernCommentReplyBoxPosition___Hlyp6",
            "MoreCommentsView__moreDownCommentsModern____f7GZ"].forEach(suppressZIndex);
        closeSearchBox();
    }
    // eslint-disable-next-line max-len
    document.getElementsByClassName("sdk-CallToAction-sideMenuButton-modernViewer")[0].click();
}

const AdobeMenuActions = props => {
    const appStore = ServiceRegistry.getService("AppStore");
    const {
        showLegalNotice,
    } = appStore.configStore.getConfig();
    const uiActionConfigStore = appStore.getUIActionConfig();
    const uiConfigs = uiActionConfigStore.getUIActionConfig(UIActionPositionTypes.TOPBAR_ELLIPSES);
    const legalDialogLabelID = appStore.enableModernViewer ? "LEGAL_NOTICES_MODERN_VIEWER" : "LEGAL_NOTICES";
    return (
        <li
            className="spectrum-Menu-item sdk-SideMenu-sideMenuActionItem sdk-SideMenu-sideMenuActionItemModernViewer"
            onKeyDown={ event => {
                if (event.key === "Enter") {
                    onClickHandler();
                }
            } }
            onClick={ onClickHandler }
            role="menuitem"
            tabIndex="0"
        >
            <Provider theme={ defaultTheme } colorScheme="light">
                { showLegalNotice
                && (
                    // eslint-disable-next-line max-len
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                    <div
                        className={ classNames([SideMenuCSS.sideMenuActionItem, SideMenuCSS.iconFill]) }
                        style={
                            {
                                backgroundColor: appStore.isFWPreset || appStore.isLBPreset
                                || (appStore.isSizedPreset && appStore.inFullScreen) ? "white" : "transparent",
                            }
                        }
                    >
                        <AdobeIcon />
                        <span
                            className="spectrum-Menu-itemLabel"
                        >
                            {props.intl.formatMessage({ id: legalDialogLabelID })}
                        </span>
                    </div>
                )}
                { uiConfigs.map(iconConfig => (
                    <Button
                        className={ classNames([SideMenuCSS.sideMenuActionItem]) }
                        label={ iconConfig.label }
                        quiet
                        variant="action"
                        onPress={ () => {
                            dispatchUIActionClickEvent(iconConfig);
                        } }
                        icon={ uiActionConfigStore.getUIActionIcon(iconConfig.iconType) }
                    />
                ))}
            </Provider>
        </li>
    );
};

AdobeMenuActions.propTypes = {
    intl: PropTypes.object.isRequired,
};

AdobeMenuActions.defaultProps = {
};

export default injectIntl(AdobeMenuActions);
